import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { Contract } from '@mobi/web-native-comms'
import { AccountHeader } from '@mobi/component-library/Account'
import { Button } from '@mobi/component-library/Common/V2/Buttons/Button'
import { InfoBox } from '@mobi/component-library/Common/V2/Display/InfoBox'
import { Input } from '@mobi/component-library/Common/V2/Inputs/Input'
import { font } from '@mobi/component-library/Theme/Common'
import { spacing } from '@mobi/component-library/Theme/Common'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import { DocumentPermission } from '@mobi/account/Areas/SignUp/Components/Verification'
import { consentText } from '@mobi/account/Areas/SignUp/Components/Message'
import { MedicareCardColor } from '@mobi/api-types/src/account/signup'
import {
  medicareExpiryNext10Years,
  NUMERIC_REGEX,
  getRequiredValidationMessage,
  validateCardNumber,
  validateReferenceNumber,
  validateExpiryDay,
  isValidName,
} from '@mobi/account/Areas/SignUp/Utils/FormValidation'
import { months } from '@mobi/utils/date'
import {
  type TypeMedicareDetailsRequest,
  type TypeSignUpApplicationResponse,
  useVerifyMedicare,
} from '../../Hooks'

import {
  getSignUpState,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
} from '../../Store'
import { DocumentBodyStyled, FormLayout } from './Layout.styled'
import { SignupError } from '../SignupError'
import {
  ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT,
  isVerificationSuccessful,
  routes,
} from '../../Utils'
import { trackVerificationSuccess } from '@mobi/account/Areas/Verification/Utils/ga'
import { trackSignUpError } from '../../Utils/ga'
import { unwrapErrorMessage } from '@mobi/utils/errors'
import { NameInput } from '@mobi/account/Common'

const cardColours = [
  { value: '', label: 'Select Colour' },
  { value: 'Green', label: 'Green' },
  { value: 'Yellow', label: 'Yellow' },
  { value: 'Blue', label: 'Blue' },
]

interface MedicareProps {
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
}
export const Medicare = ({ trackOptimoveEvent }: MedicareProps) => {
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [cardNumber, setCardNumber] = React.useState('')
  const [referenceNumber, setReferenceNumber] = React.useState('')
  const [cardColour, setCardColor] = React.useState<MedicareCardColor | null>(null)
  const [expiryDay, setExpiryDay] = React.useState<number | undefined>(undefined)
  const [expiryMonth, setExpiryMonth] = React.useState('')
  const [expiryYear, setExpiryYear] = React.useState<number | undefined>(undefined)
  const signupApplication = useSelector(getSignUpState)?.signupApplication

  const [firstName, setFirstName] = React.useState('')
  const [middleName, setMiddleName] = React.useState('')
  const [surname, setSurname] = React.useState('')

  const { verifyMedicare, verifyMedicareStatus } = useVerifyMedicare()

  const isGreenMedicareCardSelected = cardColour === 'Green'

  const isMedicareCardExpiryValid =
    (isGreenMedicareCardSelected && !!expiryMonth && !!expiryYear) ||
    (!isGreenMedicareCardSelected && !!expiryDay && !!expiryMonth && !!expiryYear)

  const days = !isGreenMedicareCardSelected
    ? Array.from({ length: daysInMonth(Number(expiryYear), parseInt(expiryMonth)) }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1),
      }))
    : []

  useEffect(() => {
    if (
      (expiryDay && expiryDay > daysInMonth(Number(expiryYear), parseInt(expiryMonth))) ||
      isGreenMedicareCardSelected
    ) {
      setExpiryDay(undefined)
    }

    if (!isMedicareCardExpiryValid) {
      setValidationMessages(prev => ({ ...prev, cardExpiryError: '' }))
    } else {
      const tempDay = isGreenMedicareCardSelected ? 31 : expiryDay
      const monthIndex = parseInt(expiryMonth) - 1

      const expiryDate = new Date(Number(expiryYear), monthIndex, tempDay)
      const todayTemp = new Date()
      const today = new Date(todayTemp.getFullYear(), todayTemp.getMonth(), todayTemp.getDate())
      setValidationMessages(prev => ({
        ...prev,
        cardExpiryError: expiryDate < today ? 'Enter a valid expiry date.' : '',
      }))
    }
  }, [expiryDay, expiryMonth, expiryYear, isGreenMedicareCardSelected, isMedicareCardExpiryValid])

  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const allowToProceed =
    hasValidEntries &&
    givePermission &&
    !!cardNumber &&
    !!referenceNumber &&
    cardColour !== undefined &&
    (isGreenMedicareCardSelected || !!expiryDay) &&
    !!expiryMonth &&
    !!expiryYear &&
    isValidName(firstName) &&
    isValidName(middleName, true) &&
    isValidName(surname)

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  const onSubmit = () => {
    if (
      !signupApplication ||
      !signupApplication.applicationReferenceNumber ||
      !signupApplication.enquiryId ||
      !allowToProceed
    ) {
      return
    }
    const medicare: TypeMedicareDetailsRequest = {
      birthdate: signupApplication.birthdate,
      firstName: firstName,
      surname: surname,
      middleName: middleName,
      email: signupApplication.email,
      mobileNumber: signupApplication.mobileNumber,
      residentialAddress: signupApplication.residentialAddress,
      previousAddress: signupApplication.previousAddress,
      enquiryId: signupApplication.enquiryId,
      applicationReferenceNumber: signupApplication.applicationReferenceNumber,
      medicareDetails: {
        cardNumber,
        referenceNumber,
        cardColour: cardColour?.toString() ?? '',
        expiryDay: expiryDay ?? 1,
        expiryMonth: expiryMonth ? parseInt(expiryMonth) : 1,
        expiryYear,
      },
      marketingOptInIndicator: signupApplication.marketingOptInIndicator,
      termsAndConditionsText: consentText,
      termsAndConditionsConsent: givePermission,
    }
    dispatch(setSignupApplication({ ...signupApplication, firstName, middleName, surname }))
    verifyMedicare(medicare, {
      onSuccess: verification => {
        if (signupApplication.applicationReferenceNumber != null) {
          const verificationResponse: TypeSignUpApplicationResponse = {
            applicationReferenceNumber: signupApplication.applicationReferenceNumber,
            enquiryId: verification.enquiryId,
            duplicateExists: false,
            isDuplicateClosed: false,
            isVerified: verification.isVerified,
            unVerifiedReason: verification.unVerifiedReason,
            potentialFraudMatch: verification.potentialFraudMatch,
            potentialSanctionedMatch: verification.potentialSanctionedMatch,
          }
          dispatch(setSignupVerification(verificationResponse))
          const isSuccessful = isVerificationSuccessful(verificationResponse)

          if (isSuccessful) {
            trackVerificationSuccess('Verified Identification', 'medicare', cardColour)
            trackOptimoveEvent(ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT)
            navigate(routes.password)
            return
          }

          dispatch(setNumberOfAttemptsToVerify())
          if (
            verificationResponse.duplicateExists ||
            verificationResponse.potentialSanctionedMatch ||
            !verificationResponse.isVerified
          ) {
            navigate(routes.signUpUnsuccessful)
            return
          }
        }
      },
      onError: error => {
        trackSignUpError(location.pathname, unwrapErrorMessage(error))
        setShowErrorPopup(true)
      },
    })
  }

  if (!signupApplication) {
    return null
  }

  return (
    <>
      <FormLayout>
        <AccountHeader
          alignItems='start'
          title='Enter your Medicare details'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your details as they appear on your ID document.
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <DocumentBodyStyled>
          <NameInput
            firstName={firstName}
            middleName={middleName}
            lastName={surname}
            setFirstName={setFirstName}
            setMiddleName={setMiddleName}
            setLastName={setSurname}
            isDisabled={verifyMedicareStatus === 'pending'}
          />
          <Input
            name='medicare-card-number'
            label='Medicare card number'
            placeholder='Enter card number'
            isDisabled={verifyMedicareStatus === 'pending'}
            value={cardNumber}
            onChange={e => {
              setCardNumber(e.target.value.replace(NUMERIC_REGEX, ''))
              setValidationMessages(prev => ({ ...prev, cardNumber: '' }))
            }}
            onFocus={() => resetFieldValidation('cardNumber')}
            onBlur={() =>
              setValidationMessages(prev => ({
                ...prev,
                cardNumber: validateCardNumber(cardNumber),
              }))
            }
            isInvalid={!!validationMessages.cardNumber}
            helpText={validationMessages.cardNumber}
            maxLength={10}
            inputMode='numeric'
            pattern='[0-9]*'
          />
          <Input
            name='medicare-reference-number'
            label='Medicare reference number'
            placeholder='Enter reference number'
            isDisabled={verifyMedicareStatus === 'pending'}
            value={referenceNumber}
            onChange={e => {
              setReferenceNumber(e.target.value.replace(NUMERIC_REGEX, ''))
              setValidationMessages(prev => ({ ...prev, referenceNumber: '' }))
            }}
            onFocus={() => resetFieldValidation('referenceNumber')}
            onBlur={() =>
              setValidationMessages(prev => ({
                ...prev,
                referenceNumber: validateReferenceNumber(referenceNumber),
              }))
            }
            isInvalid={!!validationMessages.referenceNumber}
            helpText={validationMessages.referenceNumber}
            type='text'
            maxLength={1}
            inputMode='numeric'
            pattern='[0-9]*'
          />
          <DropdownInput
            name='medicare-card-colour'
            label='Card colour'
            placeholder='Select colour'
            value={cardColour === null ? '' : cardColour.toString()}
            isDisabled={verifyMedicareStatus === 'pending'}
            onChange={event => {
              const val = event.target.value
              setCardColor(val as MedicareCardColor | null)
              resetFieldValidation('cardColor')
            }}
            onBlur={() =>
              setValidationMessages(prev => ({
                ...prev,
                cardColor: getRequiredValidationMessage(cardColour?.toString()),
              }))
            }
            isInvalid={!!validationMessages.cardColor}
            helpText={validationMessages.cardColor}
          >
            {cardColours.map(colour => (
              <option key={colour.value} value={colour.value} disabled={colour.value === ''}>
                {colour.label}
              </option>
            ))}
          </DropdownInput>
          <div>
            <LabelStyled>Expiry date</LabelStyled>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: spacing.smx1,
                width: '100%',
              }}
            >
              {!isGreenMedicareCardSelected && (
                <DropdownInput
                  name='medicare-expiry-day'
                  placeholder='Day'
                  value={expiryDay?.toString() || ''}
                  isDisabled={verifyMedicareStatus === 'pending'}
                  onChange={e => {
                    setExpiryDay(parseInt(e.target.value) || undefined)
                    resetFieldValidation('expiryDay')
                  }}
                  onBlur={() =>
                    setValidationMessages(prev => ({
                      ...prev,
                      expiryDay: validateExpiryDay(expiryDay, isGreenMedicareCardSelected),
                    }))
                  }
                  isInvalid={!!validationMessages.expiryDay}
                  helpText={validationMessages.expiryDay}
                  style={{ flex: 1 }}
                >
                  <option value=''>Day</option>
                  {days.map(d => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </DropdownInput>
              )}
              <DropdownInput
                name='medicare-expiry-month'
                placeholder='Month'
                value={expiryMonth}
                isDisabled={verifyMedicareStatus === 'pending'}
                onChange={e => {
                  setExpiryMonth(e.target.value)
                  resetFieldValidation('expiryMonth')
                }}
                onBlur={() =>
                  setValidationMessages(prev => ({
                    ...prev,
                    expiryMonth: getRequiredValidationMessage(expiryMonth),
                  }))
                }
                isInvalid={!!validationMessages.expiryMonth}
                helpText={validationMessages.expiryMonth}
                style={{ flex: 1 }}
              >
                <option value=''>Month</option>
                {months.map(m => (
                  <option key={m.number} value={m.number}>
                    {m.name}
                  </option>
                ))}
              </DropdownInput>
              <DropdownInput
                name='medicare-expiry-year'
                placeholder='Year'
                value={expiryYear?.toString() || ''}
                isDisabled={verifyMedicareStatus === 'pending'}
                onChange={e => {
                  setExpiryYear(parseInt(e.target.value) || undefined)
                  resetFieldValidation('expiryYear')
                }}
                onBlur={() =>
                  setValidationMessages(prev => ({
                    ...prev,
                    expiryYear: getRequiredValidationMessage(expiryYear?.toString()),
                  }))
                }
                isInvalid={!!validationMessages.expiryYear}
                helpText={validationMessages.expiryYear}
                style={{ flex: 1 }}
              >
                <option value=''>Year</option>
                {medicareExpiryNext10Years().map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </DropdownInput>
            </div>
          </div>
          {validationMessages.cardExpiryError && (
            <div style={{ color: 'red', marginTop: '0.5rem' }}>
              {validationMessages.cardExpiryError}
            </div>
          )}
          <div style={{ paddingTop: spacing.smx1 }}>
            <DocumentPermission
              isBusy={verifyMedicareStatus === 'pending'}
              onChange={e => setGivePermission(e.target.checked)}
            />
          </div>
        </DocumentBodyStyled>
        <footer>
          <Button
            isFullWidth
            disabled={!allowToProceed}
            isLoading={verifyMedicareStatus === 'pending'}
            onClick={onSubmit}
          >
            Continue
          </Button>
        </footer>
      </FormLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}

// =============
// Local Helpers
// =============

function daysInMonth(year: number, month: number) {
  if (!month || month < 1 || month > 12) {
    return 31
  }
  const currentYear = new Date().getFullYear()

  if (!year || year < currentYear) {
    year = currentYear
  }

  //day 0 gives us number of days in previous month
  return new Date(year, month, 0).getDate()
}

// ======
// Styles
// ======

const LabelStyled = styled.label({
  display: 'block',
  fontWeight: font.weight.medium,
  ...font.size.lg,
  marginBottom: spacing.smx1,
})
